import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../api/queryKeys'
import {
  bulkInviteUsers,
  checkNewEmailStatus,
  checkUserExists,
  createUser,
  createUserGroup,
  deleteUser,
  deleteUserGroup,
  fetchUserDetail,
  fetchBulkUserUploadStatus,
  fetchUserGroupEditInit,
  fetchUserGroupCreateInit,
  fetchUserGroups,
  fetchUsers,
  inviteUser,
  reinviteUser,
  recreateUser,
  revokeUser,
  downloadUsers,
  unlockUser,
  resetUserAuthenticator,
  uploadUsers,
  updateOrg2FA,
  updateUser,
  updateUser2FA,
  updateUserGroup,
} from '../api/users'
import type { UserFormValues, UserGroupFormValues } from '../types/users'

function invalidateUsers(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
}

function invalidateUserGroups(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.userGroups.all })
}

export function useUsers(filters: { fullName?: string; hideInactive?: boolean; clientId?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.users.list({
      name: filters.fullName,
      status: filters.hideInactive ? 'active-only' : 'all',
      clientId: filters.clientId || '',
    }),
    queryFn: () => fetchUsers(filters),
  })
}

export function useUserDetail(userId: string) {
  return useQuery({
    queryKey: ['users', 'detail', userId] as const,
    queryFn: () => fetchUserDetail(userId),
    enabled: !!userId,
  })
}

export function useDownloadUsersMutation() {
  return useMutation({
    mutationFn: (clientId: string) => downloadUsers(clientId),
  })
}

export function useUploadUsersMutation() {
  return useMutation({
    mutationFn: (payload: Parameters<typeof uploadUsers>[0]) => uploadUsers(payload),
  })
}

export function useBulkUserUploadStatus(queueId: string, enabled = true) {
  return useQuery({
    queryKey: ['users', 'bulkUploadStatus', queueId] as const,
    queryFn: () => fetchBulkUserUploadStatus(queueId),
    enabled: enabled && !!queueId,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status
      if (!status) return 3000
      return status === 'Completed' || status === 'Failed' ? false : 3000
    },
  })
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UserFormValues) => createUser(payload),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useRecreateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UserFormValues) => recreateUser(payload),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UserFormValues) => updateUser(payload),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useCheckUserExistsMutation() {
  return useMutation({
    mutationFn: (email: string) => checkUserExists(email),
  })
}

export function useCheckNewEmailStatusMutation() {
  return useMutation({
    mutationFn: (email: string) => checkNewEmailStatus(email),
  })
}

export function useBulkInviteUsersMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userIds: string[]) => bulkInviteUsers(userIds),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useReinviteUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => reinviteUser(userId),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useInviteUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => inviteUser(userId),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useRevokeUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => revokeUser(userId),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useResetUserAuthenticatorMutation() {
  return useMutation({
    mutationFn: (userId: string) => resetUserAuthenticator(userId),
  })
}

export function useUnlockUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => unlockUser(userId),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useUpdateUser2FAMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { UserId: string; DefaultClientId: string; IsTwoFactorLogin: boolean }) => updateUser2FA(payload),
    onSuccess: () => invalidateUsers(queryClient),
  })
}

export function useUpdateOrg2FAMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { isTwoFactorLogin: boolean; clientId: string }) => updateOrg2FA(payload),
    onSuccess: (_data, variables) => {
      // The server flips IsTwoFactorLogin for every user in the client, so the
      // per-row 2FA switches go stale alongside the org-level flag.
      invalidateUsers(queryClient)
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(variables.clientId) })
    },
  })
}

export function useUserGroups(
  filters: { name?: string; clientId?: string } = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.userGroups.list(filters),
    queryFn: () => fetchUserGroups(filters),
    enabled: options?.enabled ?? true,
  })
}

export function useUserGroupEditInit(groupId: string) {
  return useQuery({
    queryKey: queryKeys.userGroups.detail(groupId),
    queryFn: () => fetchUserGroupEditInit(groupId),
    enabled: !!groupId,
  })
}

export function useUserGroupCreateInit(enabled: boolean, clientId?: string) {
  return useQuery({
    queryKey: queryKeys.userGroups.createInit(clientId),
    queryFn: () => fetchUserGroupCreateInit(clientId),
    enabled,
  })
}

export function useCreateUserGroupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UserGroupFormValues) => createUserGroup(payload),
    onSuccess: () => invalidateUserGroups(queryClient),
  })
}

export function useUpdateUserGroupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UserGroupFormValues) => updateUserGroup(payload),
    onSuccess: (_data, variables) => {
      invalidateUserGroups(queryClient)
      if (variables.groupId) {
        queryClient.removeQueries({ queryKey: queryKeys.userGroups.detail(variables.groupId) })
      }
    },
  })
}

export function useDeleteUserGroupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) => deleteUserGroup(groupId),
    onSuccess: () => invalidateUserGroups(queryClient),
  })
}

